/**
 * Navigation Loader for Next.js
 *
 * Location: src/lib/navigation/get-navigation.ts
 *
 * Re-exports the generated navigation structure and provides
 * helpers for path lookup and breadcrumb generation.
 */

import {
  isNavigationLink,
  isNavigationLinkGroup,
  isExternalUrl,
  type SidebarNavigationLink,
  type SidebarNavigationLinkOrGroup,
  type SidebarNavigationSectionLabel,
  type SidebarNavigationStructure,
} from "@/components/pages/docs/navigation/navigation";

import { sidebarNavigation, navigationBySection } from "@/app/(core)/docs/generated/navigation.generated";

export { sidebarNavigation, navigationBySection };

export function getSection(section: string): SidebarNavigationStructure {
  return navigationBySection[section] ?? [];
}

export type DocPathEntry = {
  href: string;
  title: string;
  lastUpdated?: string;
};

/**
 * Get all internal documentation paths with title and lastUpdated metadata.
 */
export function getAllDocPaths(): DocPathEntry[] {
  const paths: DocPathEntry[] = [];

  function extract(
    items: (SidebarNavigationSectionLabel | SidebarNavigationLinkOrGroup)[],
  ) {
    for (const item of items) {
      if (isNavigationLink(item)) {
        if (!isExternalUrl(item.href)) {
          paths.push({
            href: item.href,
            title: item.title,
            lastUpdated: item.lastUpdated,
          });
        }
      } else if (isNavigationLinkGroup(item)) {
        extract(item.children);
      }
    }
  }

  extract(sidebarNavigation);
  return paths;
}

/**
 * Find a navigation link by its href.
 */
export function findNavItemByHref(
  targetHref: string,
): SidebarNavigationLink | null {
  function search(
    items: (SidebarNavigationSectionLabel | SidebarNavigationLinkOrGroup)[],
  ): SidebarNavigationLink | null {
    for (const item of items) {
      if (isNavigationLink(item) && item.href === targetHref) {
        return item;
      }
      if (isNavigationLinkGroup(item)) {
        const found = search(item.children);
        if (found) return found;
      }
    }
    return null;
  }

  return search(sidebarNavigation);
}

/**
 * Get breadcrumb trail for a given href.
 * Searches the provided structure, or falls back to the default sidebarNavigation.
 */
export function getBreadcrumbs(
  targetHref: string,
  structure: SidebarNavigationStructure = sidebarNavigation,
): (
  | SidebarNavigationLink
  | SidebarNavigationLinkOrGroup
  | SidebarNavigationSectionLabel
)[] {
  type AnyItem = SidebarNavigationSectionLabel | SidebarNavigationLinkOrGroup;
  const breadcrumbs: AnyItem[] = [];

  function search(items: AnyItem[], trail: AnyItem[]): boolean {
    for (const item of items) {
      const currentTrail = [...trail, item];

      if (isNavigationLink(item) && item.href === targetHref) {
        breadcrumbs.push(...currentTrail);
        return true;
      }

      if (isNavigationLinkGroup(item)) {
        if (search(item.children, currentTrail)) {
          return true;
        }
      }
    }
    return false;
  }

  search(structure, []);
  return breadcrumbs;
}
